import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import cx from 'classnames';
import { createSelector } from 'reselect';
import { Link as LinkTag, Route } from 'react-router-dom';

import { Urls, AppConfig, ServiceTypes } from 'constants/constants';
import { didPropChange } from 'utils/props';

import Channel, { selectChannel, twitchUrl, selectChannels } from 'store/models/Channel';
import Account, { selectCurrentAccount, selectAccountBots, getChannelSettings, getChatFrequency, getChannelFrequency, getChannelTags, getAllTags, hasChannelTag } from 'store/models/Account';
import Overlay, { selectOverlays, overlayUrl } from 'store/models/Overlay';
import { selectCurrentUser, selectUsers } from 'store/models/User';
import Link, { selectChannelLinkUrls, selectLinks } from 'store/models/Link';
import RouteState from 'store/RouteState';
import UIState from 'store/UIState';
import { selectApiHost, selectConfigItem } from 'store/AppState';


import { withForm } from 'components/forms/Form';
import { ContentCenter, Title, Header, HeaderLeft, HeaderRight, Breadcrumbs } from 'components/Content';
import ScrollToTop from 'components/ScrollToTop';
import Card, { CardHead, CardTitle, CardInputItem, CardItem, ItemLabel, ItemInput } from 'components/Card';
import SwitchInput from 'components/forms/SwitchInput';
import Label from 'components/forms/Label';
import ActionBar from 'components/ActionBar';
import { Button, SaveButton, SmallButton, TextButton, AddButton, CancelButton, SubButton, ButtonList } from 'components/Button';
import CopyInput from 'components/forms/CopyInput';
import TextInput, { NumberInput } from 'components/forms/TextInput';
import { ErrorList } from 'components/forms/Error';
import ChannelCard from 'components/ChannelCard';
import ChannelSelect from 'components/ChannelSelect';
import UserSelect from 'components/UserSelect';
import EmptyItem from 'components/EmptyItem';
import * as Icons from 'components/Svg';
import { TagList, ChannelGroupTag } from 'components/Tag';


// ======================
// FORM
// ====================== 
const SCHEMA = {
  disableObsCheck: {},
  enableChatbot: {},
  botFrequency: {
    transformIn: v => {
      return _.isNil(v) ? "" : v / 60000;
    },
    transformOut: v => {
      let n = parseInt(v);
      v = (_.isNaN(n) || n === 0) ? null : n * 60000;
      console.log("transformOut", v)
      return v;
    },
  }
}


// ======================
// STORE
// ======================

const actions = {
  fetchData: channelId => async (dispatch, getState) => Promise.all([
    dispatch(Channel.actions.fetchChannel(channelId)),
    dispatch(Channel.actions.fetchChannels()),
    dispatch(Account.actions.fetchAccount()),
    dispatch(Account.actions.fetchChatBots()),
    dispatch(Overlay.actions.fetchOverlaysByAccount()),
    dispatch(Link.actions.fetchLinks()),
    dispatch(Link.actions.fetchChannelLinkUrls(channelId)),
  ]),
  navigate: RouteState.actions.navigate,
  back: RouteState.actions.back,
  showModal: UIState.actions.showModal,
  updateSettings: Channel.actions.updateChannelSettings,
  updateChannelBot: Account.actions.updateChannelBotAccount,
}

const selectors = createSelector(
  selectCurrentUser,
  (_, props) => props.channelId,
  (state, props) => selectChannel(state, props.channelId),
  selectCurrentAccount,
  state => selectLinks(state),
  (state, props) => selectChannelLinkUrls(state, props.channelId),
  selectOverlays,
  selectChannels,
  selectApiHost,
  state => selectAccountBots(state) |> selectUsers(state, #),
  (user, channelId, channel, account, links, linkUrls, overlays, channels, apiHost, botUsers) => {
    let overlayUrls = _.mapValues(overlays, o => overlayUrl(apiHost, user, o, channel?.serviceName));
    botUsers = _.map(botUsers, u => 
        u._id === account?.settings?.botUserId 
          ? ({ ...u, username: `${u.username} (default)` }) 
          : u);
    let settings = getChannelSettings(account, channelId);
    let botUserId = settings?.botUserId ?? account?.settings?.botUserId;
    return {
      user,
      channelId,
      channel,
      account,
      overlays, // TODO: multiple overlays
      overlayUrls,
      settings,
      links: _.keyBy(links, '_id'),
      linkUrls: linkUrls |> _.values |> _.reduce(#, _.merge),
      channels,
      botUsers,
      botUserId,
    }
  }
)


// ======================
// COMPONENT
// ====================== 
const ChannelSettingsScreen = ({ match, values, errors, onInputChange, setDefaults, setValues, setSchema, hasEdits, validateForm, setErrors  }) => {
  let { user, channelId, channel, account, overlays, settings, links, linkUrls, overlayUrls, channels, botUsers, botUserId,} = useSelector(s => selectors(s, {channelId: match.params.channelId}));
  let [loading, setLoading] = useState(false);
  let [saving, setSaving] = useState(false);
  let [tagNameValue, setTagNameValue] = useState();
  let [showFrequency, setShowFrequency] = useState(false);
  let [showAddTag, setShowAddTag] = useState(false);
  let [showCreateTag, setShowCreateTag] = useState(false);
  const dispatch = useDispatch();

  const defaultFrequency = getChatFrequency(account) / 60000;
  const channelFrequency = getChannelFrequency(account, channel?._id);
  let _frequencyRef = null
  let _onFrequencyRef = n => _frequencyRef = n;
  const tags = getChannelTags(account, channel?._id);
  const allTags = getAllTags(account);
  const hasTags = !_.isEmpty(tags);
  const hasMultipleOverlays = _.size(overlays) > 1;

  const fetchData = () => Promise.all([
    dispatch(Channel.actions.fetchChannel(channelId)),
    dispatch(Channel.actions.fetchChannels()),
    dispatch(Account.actions.fetchAccount()),
    dispatch(Account.actions.fetchChatBots()),
    dispatch(Overlay.actions.fetchOverlaysByAccount()),
    dispatch(Link.actions.fetchLinks()),
    dispatch(Link.actions.fetchChannelLinkUrls(channelId)),])

  useEffect(() => {
    fetchData(channelId);
    (async () => await setSchema(SCHEMA))();
    if(settings) {
      setDefaults(settings);
      if(!_.isNil(settings.botFrequency))
        setShowFrequency(true);
    }
  }, [])

  useEffect(() => {
    setDefaults(settings);
    if(!_.isNil(settings?.botFrequency))
      setShowFrequency(true);
  }, [settings]);

  useEffect(() => {
    fetchData(channelId);
  }, [channelId]);

  useEffect(() => {
    if(!hasTags) {
      //setShowAddTag(false);
      //setShowCreateTag(false);
    }
  }, [account]);

  const onSave = async () => {
    if(!hasEdits())
      return dispatch(RouteState.actions.back());

    let values = await validateForm();

    try {
      setSaving(true);
      await dispatch(Channel.actions.updateChannelSettings(channelId, values));
      dispatch(RouteState.actions.back());
    }
    catch(err) {
      setErrors({ server: err.message });
    } 
    finally {
      setSaving(false);
    }
  }

  const onDeleteChannel = () => dispatch(Account.actions.removeChannel(channelId));
  const onTwitchView = () => window.open(twitchUrl(channel))
  const onFocus = e => e.target.select()
  const onChannelChange = channelId => dispatch(RouteState.actions.navigate(Urls.CHANNEL_SETTINGS_ROUTE, { channelId }));
  const onTagInputChange = value => setTagNameValue(value);
  const onShowAddTag = () => setShowAddTag(!showAddTag);
  const onShowCreateTag = () => setShowCreateTag(!showCreateTag);
  const onRemoveChannelTag = async tagId => {
    await dispatch(Account.actions.removeChannelTag(channelId, tagId));
    dispatch(Link.actions.fetchChannelLinkUrls(channelId));
  }
  const onAddChannelTag = async tagId => {
    await dispatch(Account.actions.addChannelTag(channelId, tagId));
    dispatch(Link.actions.fetchChannelLinkUrls(channelId));
  };
  const onCreateTag = async () => { 
    try {
      await dispatch(Account.actions.createTag(tagNameValue));
      setTagNameValue();
      setShowCreateTag(false);
    } catch (err) {
      //NOOP
    }
  };
  const onShowFrequency = () => {
    setShowFrequency(true);
    setValues({ botFrequency: getChatFrequency(account) });
  }
  const onSetDefaultFrequency = () => {
    setShowFrequency(false);
    setValues({ botFrequency: null });
  }
  const onBotChange = userId => dispatch(Account.actions.updateChannelBotAccount(channelId, userId))
  const onVisitAccountSettings = () => dispatch(RouteState.actions.navigate(Urls.ACCOUNT_SETTINGS))

  return (
    <ContentCenter className="channel-settings">
      <ScrollToTop />
      <Header split={true}>
        <HeaderLeft>
          <Breadcrumbs items={{ channels: Urls.CHANNELS }} />
          <Title>Channel Settings</Title>
        </HeaderLeft>
        <HeaderRight>
          <ChannelSelect 
            channels={channels} 
            size="m" 
            selected={channel?._id} 
            onChange={onChannelChange} />
        </HeaderRight>
      </Header>

      <ErrorList errors={errors} />

      <Card>
        <CardHead>
          <CardTitle>Channel Groups</CardTitle>
        </CardHead>
        <CardItem>
          <p className="tag-desc">Add group tags to your channels to aggregate their data. Perfect for grouping teams, regions, or types of streamers.</p>
          { hasTags 
            ? <TagList>
                { _.map(tags, tag => <ChannelGroupTag key={tag.tagId} tag={tag} onDelete={onRemoveChannelTag} />)}
              </TagList>
            : null}
          { !showAddTag && 
          <AddButton size="s" onClick={onShowAddTag}>Add Tag</AddButton>}
        </CardItem>
        { showAddTag && 
        <CardItem className="add-tag">
          { !showCreateTag && <>
            <label className="existing-label">Select a Tag</label>
            <TagList>
              { _.map(allTags, tag => {
                const disabled = hasChannelTag(account, channelId, tag.tagId);
                return <ChannelGroupTag key={tag.tagId} tag={tag} disabled={disabled} onAdd={!disabled ? onAddChannelTag : null} onClick={!disabled ? onAddChannelTag : null} />})}
            </TagList>
            <ButtonList size="s">
              <Button onClick={onShowAddTag} size="s">Done</Button>
              <Button onClick={onShowCreateTag} size="s" color="sub"><Icons.PlusIcon size="s" /> Create New Tag</Button>
            </ButtonList>
          </>}
          { showCreateTag && <>
          <TextInput className="tag-name" value={tagNameValue} onChange={onTagInputChange} autoFocus={true}/>
          <ButtonList size="s">
            <Button size="s" onClick={onCreateTag}>Save</Button>
            <CancelButton size="s" onClick={onShowCreateTag}>Cancel</CancelButton>
          </ButtonList></>}
        </CardItem>}
      </Card>

      <Card>
        <CardHead>
          <CardTitle>Slideshow</CardTitle>
        </CardHead>
        <CardInputItem>
          <ItemLabel>
            <Label>OBS Code</Label>
          </ItemLabel>
          <ItemInput>
            <div className={cx("step step-1", hasMultipleOverlays ? "multiple-overlays" : "")}>
              <div className="label"><strong>Step 1</strong> - Add Slideshow URL to Browser Source Plugin for OBS</div>
              { _.map(overlayUrls, (overlayUrl, overlayId) => {
                  let overlay = overlays[overlayId];
                  if(!overlayUrl) return null;
                  return (<span key={overlayId}>
                    { hasMultipleOverlays ? 
                      <div className="label overlay">{overlay?.name}</div> 
                      : null}
                    <div className="url-input" key={overlayId} >
                      <CopyInput
                        value={overlayUrl}
                        color="sub"
                        onFocus={onFocus} />
                    </div></span>)
              })}
              
            </div>
            <div className="step step-2">
              <div className="label"><strong>Step 2</strong> - Check 'Shutdown Source' setting</div>
              <img src="/assets/images/obs_shutdown_setting.png" />
            </div>
          </ItemInput>
        </CardInputItem>
        <CardInputItem>
          <ItemLabel>
            <Label>OBS Security Check</Label>
          </ItemLabel>
          <ItemInput>
            <SwitchInput
              name="disableObsCheck"
              value={values.disableObsCheck}
              transformIn={v => !v}
              transformOut={v => !v}
              onChange={onInputChange}
              helper={"Check to ensure the slideshow is running in OBS (disabling this is not common)"} />
          </ItemInput>
        </CardInputItem>
      </Card>

      { channel?.service === ServiceTypes.TWITCH &&
      <Card>
        <CardHead>
          <CardTitle>Links</CardTitle>
        </CardHead>
        <CardInputItem>
          <ItemLabel>
            <Label>Link URLs</Label>
          </ItemLabel>
          <ItemInput>
          { _.size(linkUrls) === 0 
            ? <div>
                <p>No links are being targeted to this channel.</p>
              </div>
            : <>
                {_.map(linkUrls, (url, linkId) => {
                    let link = links[linkId]
                    if(!url) return null;
                    return (
                      <div key={linkId} className="link-item">
                        <div className="link-name">
                          <strong>{link?.name}</strong>
                          <span>{link?.url}</span>
                        </div>
                        <CopyInput value={url} color="sub" />
                      </div>)
                  })}
              </>}
            
          </ItemInput>
        </CardInputItem>
      </Card>}
    
      { channel?.service === ServiceTypes.TWITCH &&
      <Card>
        <CardHead>
          <CardTitle>Chat Bot</CardTitle>
        </CardHead>
        <CardInputItem>
          <ItemLabel>
            <Label>Run Chatbot</Label>
          </ItemLabel>
          <ItemInput>
            <SwitchInput
              name="enableChatbot"
              value={values.enableChatbot}
              onChange={onInputChange}
              helper={"Automatically post campaign links in this channel's chat"} />
          </ItemInput>
        </CardInputItem>
        <CardInputItem className={cx({ disabled: !values.enableChatbot })}>
          <ItemLabel>
            <Label>Twitch Account</Label>
          </ItemLabel>
          <ItemInput>
            { _.size(botUsers) === 0 
              ? <div>
                  <p>There are no twitch users connected to your account. Visit your <LinkTag onClick={onVisitAccountSettings}>Account Settings</LinkTag> to add one.</p>
                </div>
              : <div className="bot-account">
                  <div className="bot-account-selector">
                    <UserSelect 
                      users={_.values(botUsers)} 
                      size="s" 
                      selected={botUserId} 
                      onChange={onBotChange} />
                  </div>
                  <div className="bot-helper">
                    This twitch account will be used to post links in chat.
                  </div>
                </div>}
            
          </ItemInput>
        </CardInputItem>
        <CardInputItem className={cx({ disabled: !values.enableChatbot })}>
          <ItemLabel>
            <Label>Chat Frequency</Label>
          </ItemLabel>
          <ItemInput>
            <div className="frequency-wrap">
              <div className={cx("channel-frequency", { show: !_.isNil(channelFrequency) ||  showFrequency })}>
                <NumberInput
                  ref={_onFrequencyRef}
                  name="botFrequency"
                  label="Channel Setting"
                  value={values.botFrequency}
                  onChange={onInputChange}
                  helper={"How frequently this channel will post links. (in minutes)"}
                  postfix="m" />
                { showFrequency && 
                <TextButton onClick={onSetDefaultFrequency}>Reset To Default</TextButton>}
              </div>
              <div className="account-default">
                <div className="account-frequency-value">
                  <label>Account Default:</label>
                  <strong>{defaultFrequency} minute</strong>
                </div>
                { !showFrequency && 
                <SmallButton onClick={onShowFrequency}>Customize for channel</SmallButton>}
               
              </div>
            </div>
          </ItemInput>
        </CardInputItem>
        <CardInputItem className={cx({ disabled: !values.enableChatbot })}>
          <ItemLabel>
            <Label>Twitch Permissions</Label>
          </ItemLabel>
          <ItemInput>
            <div className={cx("mod-instructions")}>
              <div className="mod-label">
                <strong>Required!</strong> - Add your bot as a moderator using "/mod XXXX" twitch command.
              </div>
              { //<CopyInput value={`/mod ${chatbot}`} color="sub" />
              }
              <TextButton className="view" href={twitchUrl(channel)} target="_blank">View Channel <Icons.ExternalIcon size="m" className="right" /></TextButton>
            </div>
          </ItemInput>
          
        </CardInputItem>
      </Card>}
      <ActionBar type="fixed">
        <SaveButton onClick={onSave} />
      </ActionBar>
    </ContentCenter>)
}
export default ChannelSettingsScreen 
  |> withForm;

